import React, { useCallback, useMemo } from "react";
import Icon from "components/atom/Icon.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useMount } from "react-use";
import { UserType } from "globals/types/models.ts";
import { useAppSelector } from "config/store";
import { useTranslation } from "react-i18next";
import { USER_TYPES } from "globals/constants.ts";
import LoadingContainer from "components/container/LoadingContainer.tsx";

function ConnectedContainer(props: { children: React.ReactNode, title: string, isLoading?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currentUser: UserType | null = useAppSelector(state => state.auth.user);
  const currentUserIsCompleted: boolean = useAppSelector(state => state.auth.userIsCompleted);

  useMount(() => {

    //go to admin page if we are not in admin page
    if (currentUser && currentUser.isAdmin) {
      if (!location.pathname.startsWith("/profile/admin")) {
        navigate("/profile/admin");
        return;
      }
    }

    //block access if user is not completed and has not a verified email
    if (currentUser && currentUser.type === USER_TYPES.EVENT_MANAGER && !currentUser.isAdmin && currentUser.unverifiedEmail && currentUser.email === currentUser.unverifiedEmail) {
      navigate({
        pathname: "/map",
        hash: "#/auth/verify-email"
      }, { state: { email: currentUser.unverifiedEmail, redirect_to: location.pathname, coming_from: "click" } });
    }
  });

  const showSettings = useMemo(() => {
    if (location.pathname === "/profile") {
      return true;
    }

    return false;
  }, [location.pathname]);

  const onClickSettings = useCallback(() => {
    navigate("/profile/me");
  }, [navigate]);

  const onClickGoBack = useCallback(() => {
    if (location.pathname.startsWith("/profile/me/plans")) {
      navigate("/profile/me/account");
      return;
    }

    if (location.pathname.startsWith("/profile/me/")) {
      navigate("/profile/me");
      return;
    }


    navigate("/profile");
  }, [location.pathname, navigate]);

  return (
    <div className="flex h-screen w-full flex-col items-start justify-start overflow-hidden">
      <div className="w-full">
        <div className="fixed z-2 left-0 right-0 top-0 py-4 sm:py-6 bg-white container mx-auto">
          <div className="flex flex-row flex-nowrap items-center justify-center">
            <div className="flex w-24 flex-row items-center justify-start gap-4">
              {showSettings ? (
                <div className="flex flex-row items-center justify-start gap-2">
                  <button type="button" className="appearance-none" onClick={onClickSettings}>
                    <Icon name="settings" className="block min-w-6 h-6 w-6  text-gray-900" />
                  </button>
                  {!currentUserIsCompleted && (
                    <Icon name="alert-circle"
                          className="block min-w-4 w-4 h-4 sm:min-w-4 sm:h-6 sm:w-6  text-red-500" />
                  )}
                </div>
              ) : (
                <button type="button"
                        className="flex appearance-none flex-row items-center justify-center gap-1"
                        onClick={onClickGoBack}>
                  <Icon name="angle-left"
                        className="block min-w-4 w-4 h-4 sm:min-w-4 sm:h-6 sm:w-6 text-gray-900" />
                  <span className="text-base">
                    {t("goBack")}
                  </span>
                </button>
              )}

            </div>
            <div className="flex flex-1 flex-row items-center justify-center gap-4">
              <h2 className="text-base sm:text-xl text-center">
                {props.title}
              </h2>
            </div>
            <div className="flex w-24 flex-row items-center justify-end gap-4">

            </div>
          </div>
        </div>
      </div>
      <div className="flex max-h-full w-full flex-1 flex-col">
        <div className="flex h-full w-full flex-1 flex-col overflow-y-scroll pb-32 pt-20 sm:pt-24">
          <div className="container mx-auto flex w-full flex-1 flex-col">
            <LoadingContainer isLoading={props.isLoading ? props.isLoading : false}>
              {props.children}
            </LoadingContainer>
          </div>
        </div>
      </div>
    </div>
  );

}

export default ConnectedContainer;
